// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { BankUploadSummaryRow } from '@client/banks/components/shared/bank-upload-summary-list';
import summaryListStyles from '@client/banks/components/shared/bank-upload-summary-list/bank-upload-summary-list.module.css';
import type { BankPosition } from '@client/banks/types/position';
import { Typography } from '@mui/material';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import type { FC } from 'react';
import { PositionsListHeader } from './positions-list-header';
import styles from './positions-list.module.css';

export type PositionsListHeaderProps = {
  onAdd: () => void;
  onImportExcel?: () => void;
};

export type PositionSummaryItem = {
  index: number;
  position: BankPosition;
  meta: string;
  investmentLabel: string;
};

export type PositionsListProps = {
  items: PositionSummaryItem[];
  isEmpty: boolean;
  onAdd: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onImportExcel?: () => void;
};

export const PositionsList: FC<PositionsListProps> = ({
  items,
  isEmpty,
  onAdd,
  onEdit,
  onDelete,
  onImportExcel,
}) => (
  <div
    className="flex min-h-0 flex-1 flex-col"
    data-testid="bank-upload-positions-list"
  >
    <PositionsListHeader onAdd={onAdd} onImportExcel={onImportExcel} />

    <section className={styles['positions-summary']} aria-live="polite">
      <ul className={summaryListStyles['summary-list']}>
        {isEmpty ? (
          <li
            className={summaryListStyles['summary-list-empty']}
            data-testid="bank-upload-positions-list-empty"
          >
            <Typography
              variant="body2"
              className={summaryListStyles['summary-list-empty-message']}
            >
              <Translation
                i18nKey="aggregator.create-bank.data-section.empty-list-hint"
                ns="aggregator"
              />
            </Typography>
          </li>
        ) : (
          items.map(item => (
            <BankUploadSummaryRow
              key={`${item.position.iban}-${item.position.isin}-${item.index}`}
              testIdPrefix="bank-upload-position"
              index={item.index}
              title={item.position.name}
              meta={item.meta}
              valueLabel={item.investmentLabel}
              onOpen={onEdit}
              onDelete={onDelete}
            />
          ))
        )}
      </ul>
    </section>
  </div>
);

PositionsList.displayName = 'PositionsList';
